import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Utensils, Plus, Trash2, Droplet, Minus, Upload, Download, FileSpreadsheet, Loader2, Sparkles, CalendarDays, Pill, RefreshCw, Check } from "lucide-react";
import api from "@/lib/api";
import { localISODate, formatDate, LONG_DATE } from "@/lib/dates";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { Ring } from "@/components/Charts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { MEAL_PLANS, DAY_KEYS, DAY_LABELS, GOAL_INFO, dayTotals } from "@/lib/mealPlans";

const MEALS = ["breakfast", "lunch", "dinner", "snack"];
const today = () => localISODate();

export default function Nutrition() {
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("food");
  return (
    <div className="space-y-6">
      <SectionHeader title={t("nutrition")} subtitle={formatDate(new Date(), lang, LONG_DATE)} />
      <div className="flex gap-2 rounded-xl border bg-card p-1" data-testid="nutrition-tabs">
        {[["food", Utensils, t("tabFood")], ["supplements", Pill, t("tabSupplements")]].map(([key, Icon, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`nutrition-tab-${key}`}
            className={cn("flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors",
              tab === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "supplements" ? <Supplements /> : <FoodTab />}
    </div>
  );
}

function FoodTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const { data: entries } = useQuery({ queryKey: ["nutrition", today()], queryFn: async () => (await api.get("/nutrition", { params: { date: today() } })).data });
  const { data: water } = useQuery({ queryKey: ["water", today()], queryFn: async () => (await api.get("/water", { params: { date: today() } })).data });
  const { data: health } = useQuery({ queryKey: ["health"], queryFn: async () => (await api.get("/profile/health")).data });

  const totals = (entries || []).reduce((a, e) => ({
    calories: a.calories + (e.calories || 0), protein: a.protein + (e.protein || 0),
    carbs: a.carbs + (e.carbs || 0), fat: a.fat + (e.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const target = health?.calorie_target || 2200;
  const proteinTarget = health?.weight ? Math.round(health.weight * 1.8) : 140;

  const changeWater = async (ml) => { await api.post("/water", { ml }); qc.invalidateQueries({ queryKey: ["water", today()] }); };

  return (
    <div className="space-y-6">
      {/* Macro summary */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex items-center gap-5 rounded-2xl border bg-card p-5 lg:col-span-2">
          <Ring value={totals.calories} max={target} size={110} stroke={11} label={totals.calories} sublabel={`/ ${target}`} />
          <div className="flex-1 space-y-3">
            <MacroBar label={t("protein")} value={Math.round(totals.protein)} max={proteinTarget} color="hsl(var(--primary))" unit="g" />
            <MacroBar label={t("carbs")} value={Math.round(totals.carbs)} max={250} color="hsl(199 89% 55%)" unit="g" />
            <MacroBar label={t("fat")} value={Math.round(totals.fat)} max={70} color="hsl(38 92% 55%)" unit="g" />
          </div>
        </div>

        {/* Water */}
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">{t("water")}</span>
            <Droplet className="h-4 w-4 text-sky-500" />
          </div>
          <div className="mt-4 font-display text-3xl font-bold tabular-nums">{((water?.ml || 0) / 1000).toFixed(2)}<span className="text-base font-normal text-muted-foreground"> L</span></div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => changeWater(250)} className="flex-1 gap-1" data-testid="water-add"><Plus className="h-3 w-3" />250ml</Button>
            <Button size="sm" variant="outline" onClick={() => changeWater(-250)} data-testid="water-remove"><Minus className="h-3 w-3" /></Button>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-csv-btn"><Upload className="h-4 w-4" /> {t("importCsv")}</Button>
        <Button onClick={() => setOpen(true)} className="gap-2" data-testid="add-food-btn"><Plus className="h-4 w-4" /> {t("addFood")}</Button>
      </div>

      {/* Meals */}
      {MEALS.map((m) => {
        const items = (entries || []).filter((e) => e.meal === m);
        if (!items.length) return null;
        return (
          <div key={m}>
            <h3 className="mb-2 font-display font-semibold">{t(m)}</h3>
            <div className="space-y-2">
              {items.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-xl border bg-card px-4 py-3" data-testid={`food-${e.id}`}>
                  <div><div className="text-sm font-medium">{e.name}</div><div className="text-xs text-muted-foreground tabular-nums">P{e.protein || 0} · K{e.carbs || 0} · L{e.fat || 0}</div></div>
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold tabular-nums">{e.calories || 0}<span className="text-xs font-normal text-muted-foreground"> kcal</span></span>
                    <button onClick={async () => { await api.delete(`/nutrition/${e.id}`); qc.invalidateQueries({ queryKey: ["nutrition", today()] }); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {!entries?.length && <EmptyState icon={Utensils} title={t("foodLog")} description={t("startTracking")} />}

      <MealPlanner defaultGoal={health?.goal} onAdded={() => qc.invalidateQueries({ queryKey: ["nutrition", today()] })} />

      <FoodDialog open={open} onClose={() => setOpen(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["nutrition", today()] })} />
      <ImportCsvDialog open={importOpen} onClose={() => setImportOpen(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["nutrition", today()] })} />
    </div>
  );
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const meals = ["breakfast", "lunch", "dinner", "snack"];
  const first = lines[0].toLowerCase();
  const hasHeader = first.includes("nama") || first.includes("name") || first.includes("meal") || first.includes("kalori") || first.includes("calor");
  const rows = hasHeader ? lines.slice(1) : lines;
  const num = (v) => { const n = parseFloat(String(v).replace(",", ".")); return isNaN(n) ? 0 : n; };
  return rows.map((line) => {
    const cols = line.split(/[,;]/).map((c) => c.trim());
    const meal = (cols[1] || "snack").toLowerCase();
    return {
      name: cols[0] || "",
      meal: meals.includes(meal) ? meal : "snack",
      calories: num(cols[2]), protein: num(cols[3]), carbs: num(cols[4]), fat: num(cols[5]),
    };
  }).filter((r) => r.name);
}

function ImportCsvDialog({ open, onClose, onSaved }) {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error(t("csvOnly")); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result));
      if (!parsed.length) { toast.error(t("csvEmpty")); return; }
      setRows(parsed.slice(0, 200));
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csv = "nama,meal,kalori,protein,karbo,lemak\nNasi Goreng,lunch,450,12,60,15\nTelur Rebus,breakfast,78,6,1,5\nPisang,snack,105,1,27,0";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "template-nutrisi.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/nutrition/bulk", { items: rows, date: today() });
      toast.success(`${data.inserted} ${t("itemsImported")}`);
      onSaved(); close();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setImporting(false); }
  };

  const close = () => { setRows([]); onClose(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> {t("importCsv")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("importCsvHint")}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={downloadTemplate} className="flex-1 gap-2" data-testid="csv-template-btn"><Download className="h-4 w-4" /> {t("downloadTemplate")}</Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="flex-1 gap-2" data-testid="csv-choose-btn"><Upload className="h-4 w-4" /> {t("chooseFile")}</Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
          </div>
          {rows.length > 0 && (
            <div className="rounded-xl border" data-testid="csv-preview">
              <div className="border-b bg-muted/50 px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">{rows.length} {t("itemsToImport")}</div>
              <div className="max-h-56 divide-y overflow-y-auto no-scrollbar">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div><span className="font-medium">{r.name}</span> <span className="text-xs text-muted-foreground capitalize">· {t(r.meal)}</span></div>
                    <span className="tabular-nums text-xs text-muted-foreground">{r.calories} kcal · P{r.protein} K{r.carbs} L{r.fat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={doImport} disabled={!rows.length || importing} className="gap-2" data-testid="csv-import-confirm">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("import")} {rows.length ? `(${rows.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MealPlanner({ defaultGoal, onAdded }) {
  const { t, lang } = useI18n();
  const mapGoal = (g) => {
    g = (g || "").toLowerCase();
    if (/cut|lose|fat|diet|deficit/.test(g)) return "cutting";
    if (/bulk|gain|muscle|mass|surplus/.test(g)) return "bulking";
    return "maintenance";
  };
  const todayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()];
  const [goal, setGoal] = useState(mapGoal(defaultGoal));
  const [day, setDay] = useState(todayKey);
  const [adding, setAdding] = useState(false);
  const meals = MEAL_PLANS[goal][day];
  const totals = dayTotals(meals);
  const info = GOAL_INFO[goal];

  const addMeal = async (m) => {
    try {
      await api.post("/nutrition", { name: m.name, meal: m.meal, calories: m.kcal, protein: m.protein, carbs: m.carbs, fat: m.fat, date: today() });
      toast.success(t("addedToToday"));
      onAdded();
    } catch { toast.error("Error"); }
  };
  const addAll = async () => {
    setAdding(true);
    try {
      await api.post("/nutrition/bulk", { items: meals.map((m) => ({ name: m.name, meal: m.meal, calories: m.kcal, protein: m.protein, carbs: m.carbs, fat: m.fat })), date: today() });
      toast.success(t("planAddedToday"));
      onAdded();
    } catch { toast.error("Error"); } finally { setAdding(false); }
  };

  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="meal-planner">
      <div className="mb-4 flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><Sparkles className="h-5 w-5" /></span>
        <div>
          <h3 className="font-display font-semibold">{t("mealPlanTitle")}</h3>
          <p className="text-xs text-muted-foreground">{t("mealPlanSubtitle")}</p>
        </div>
      </div>

      {/* Goal toggle */}
      <div className="flex flex-wrap gap-2" data-testid="goal-toggle">
        {["cutting", "maintenance", "bulking"].map((g) => (
          <button key={g} onClick={() => setGoal(g)} data-testid={`goal-${g}`}
            className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
              goal === g ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent")}>
            {GOAL_INFO[g][lang === "id" ? "id" : "en"]}
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="rounded-md bg-accent px-2 py-0.5 font-semibold text-foreground">{info.kcal}</span>
        <span>{info.tip[lang === "id" ? "id" : "en"]}</span>
      </div>

      {/* Day tabs */}
      <div className="mt-4 flex gap-1.5 overflow-x-auto no-scrollbar pb-1" data-testid="day-tabs">
        {DAY_KEYS.map((d) => (
          <button key={d} onClick={() => setDay(d)} data-testid={`day-${d}`}
            className={cn("shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
              day === d ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground hover:text-foreground")}>
            {DAY_LABELS[lang === "id" ? "id" : "en"][d]}{d === todayKey ? " •" : ""}
          </button>
        ))}
      </div>

      {/* Day summary */}
      <div className="mt-3 flex items-center gap-4 rounded-xl border bg-background px-4 py-2.5 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground"><CalendarDays className="h-4 w-4" /> {DAY_LABELS[lang === "id" ? "id" : "en"][day]}</span>
        <span className="ml-auto font-display font-bold tabular-nums">{totals.kcal.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">kkal</span></span>
        <span className="font-display font-bold tabular-nums text-primary">{totals.protein}g <span className="text-xs font-normal text-muted-foreground">protein</span></span>
      </div>

      {/* Meals */}
      <div className="mt-3 space-y-2">
        {meals.map((m, i) => (
          <div key={i} className="flex items-center justify-between gap-3 rounded-xl border bg-background px-4 py-2.5" data-testid={`plan-meal-${i}`}>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t(m.meal)}</div>
              <div className="truncate text-sm font-medium">{m.name}</div>
              <div className="text-xs text-muted-foreground tabular-nums">{m.kcal} kkal · P{m.protein} · K{m.carbs} · L{m.fat}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => addMeal(m)} className="shrink-0 gap-1" data-testid={`plan-add-${i}`}><Plus className="h-3.5 w-3.5" /> {t("add")}</Button>
          </div>
        ))}
      </div>

      <Button onClick={addAll} disabled={adding} className="mt-3 w-full gap-2" data-testid="plan-add-all">
        {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("addAllToday")}
      </Button>
    </div>
  );
}


function Supplements() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["supplements"], queryFn: async () => (await api.get("/nutrition/supplements")).data });
  const { data: log } = useQuery({ queryKey: ["supplement-log"], queryFn: async () => (await api.get("/nutrition/supplements/log")).data });
  const [generating, setGenerating] = useState(false);
  const items = data?.items;
  const taken = log?.taken || [];

  const toggleTaken = async (name) => {
    const isTaken = taken.includes(name);
    const next = { ...(log || { taken: [] }), taken: isTaken ? taken.filter((x) => x !== name) : [...taken, name] };
    qc.setQueryData(["supplement-log"], next);
    try {
      const { data: res } = await api.post("/nutrition/supplements/log", { name, taken: !isTaken });
      qc.setQueryData(["supplement-log"], res);
      if (!isTaken && items?.length && res.taken.length === items.length) toast.success(t("suppAllTaken"));
    } catch { qc.invalidateQueries({ queryKey: ["supplement-log"] }); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/nutrition/supplements/generate");
      qc.setQueryData(["supplements"], res.data);
      toast.success(t("suppTitle"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setGenerating(false); }
  };

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="supplements-view">
      <div className="flex items-start gap-3 rounded-2xl border bg-card p-5">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary"><Pill className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-bold tracking-tight">{t("suppTitle")}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{t("suppSubtitle")}</p>
          {data?.generated_at && (
            <p className="mt-1 text-xs text-muted-foreground">{t("suppUpdated")}: {new Date(data.generated_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}</p>
          )}
        </div>
        {items?.length > 0 && (
          <Button variant="outline" size="sm" onClick={generate} disabled={generating} className="shrink-0 gap-1.5" data-testid="supp-regenerate-btn">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("suppRegenerate")}
          </Button>
        )}
      </div>

      {generating && !items?.length ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border bg-card py-16 text-center">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t("suppGenerating")}</p>
        </div>
      ) : !items?.length ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border bg-card py-14 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="h-7 w-7" /></span>
          <p className="max-w-xs text-sm text-muted-foreground">{t("suppEmpty")}</p>
          <Button onClick={generate} disabled={generating} className="gap-2" data-testid="supp-generate-btn">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("suppGenerate")}
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl border bg-card p-4" data-testid="supp-checklist">
            <div className="mb-1 flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary"><Pill className="h-4 w-4" /></span>
              <div>
                <div className="font-display text-sm font-semibold">{t("suppToday")}</div>
                <div className="text-xs text-muted-foreground">{t("suppChecklistHint")}</div>
              </div>
              <span className="ml-auto rounded-full bg-accent px-2.5 py-1 text-xs font-semibold tabular-nums">{taken.length}/{items.length}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {items.map((s, i) => {
                const isTaken = taken.includes(s.name);
                return (
                  <button key={i} onClick={() => toggleTaken(s.name)} data-testid={`supp-check-${i}`}
                    className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      isTaken ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent")}>
                    <span className={cn("grid h-3.5 w-3.5 place-items-center rounded-full border", isTaken ? "border-primary-foreground" : "border-muted-foreground")}>
                      {isTaken && <Check className="h-3 w-3" />}
                    </span>
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
          {items.map((s, i) => {
            const essential = s.priority === "essential";
            return (
              <div key={i} className="rounded-2xl border bg-card p-4" data-testid={`supp-card-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold">{s.name}</div>
                    {s.category && <div className="text-xs text-muted-foreground">{s.category}</div>}
                  </div>
                  <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide",
                    essential ? "bg-primary/15 text-primary" : "bg-accent text-muted-foreground")}>
                    {essential ? t("suppEssential") : t("suppOptional")}
                  </span>
                </div>
                {s.benefit && <p className="mt-2 text-sm leading-relaxed text-foreground/90">{s.benefit}</p>}
                <div className="mt-3 space-y-1.5 border-t pt-3 text-xs">
                  {s.dosage && <div className="flex gap-2"><span className="w-14 shrink-0 font-semibold text-muted-foreground">{t("suppDosage")}</span><span className="tabular-nums">{s.dosage}</span></div>}
                  {s.timing && <div className="flex gap-2"><span className="w-14 shrink-0 font-semibold text-muted-foreground">{t("suppTiming")}</span><span>{s.timing}</span></div>}
                  {s.note && <div className="flex gap-2"><span className="w-14 shrink-0 font-semibold text-muted-foreground">{t("suppNote")}</span><span className="text-muted-foreground">{s.note}</span></div>}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {items?.length > 0 && <p className="text-center text-xs text-muted-foreground">{t("suppDisclaimer")}</p>}
    </div>
  );
}


function MacroBar({ label, value, max, color, unit }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="tabular-nums font-medium">{value}{unit} / {max}{unit}</span></div>
      <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

function FoodDialog({ open, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", meal: "breakfast", calories: "", protein: "", carbs: "", fat: "" });
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    await api.post("/nutrition", {
      name: f.name, meal: f.meal, date: today(),
      calories: parseFloat(f.calories) || 0, protein: parseFloat(f.protein) || 0,
      carbs: parseFloat(f.carbs) || 0, fat: parseFloat(f.fat) || 0,
    });
    toast.success(t("save")); onSaved(); onClose(); setF({ name: "", meal: "breakfast", calories: "", protein: "", carbs: "", fat: "" });
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("addFood")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("name")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="food-name" /></div>
          <div><Label>{t("meal")}</Label>
            <Select value={f.meal} onValueChange={(v) => setF({ ...f, meal: v })}><SelectTrigger className="mt-1.5" data-testid="food-meal"><SelectValue /></SelectTrigger>
              <SelectContent>{MEALS.map((m) => <SelectItem key={m} value={m}>{t(m)}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[["calories", t("calories")], ["protein", "P"], ["carbs", "K"], ["fat", "L"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 tabular-nums" data-testid={`food-${k}`} /></div>
            ))}
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-food">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
